import { NumericFormatProps } from "react-number-format/types/types";
import { addMessage } from "./addMessage";
import { DateValue, RangeValue } from "@nextui-org/react";
import { getLocalTimeZone, today } from "@internationalized/date";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

export const isObjectEmpty = (obj: object): boolean => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const defaultNumericFormatProps: NumericFormatProps = {
  allowNegative: true,
  decimalScale: 2,
  decimalSeparator: ",",
  displayType: "text",
  fixedDecimalScale: true,
  prefix: "$",
  thousandSeparator: ".",
};

export const defaultQueryConfig = {
  refetchOnWindowFocus: true,
  refetchIntervalInBackground: false,
  // refetchInterval: 10000,
};

export const contactStateDictionary = {
  Aprobado: "APPROVED",
  Pendiente: "PENDING",
  Rechazado: "REJECTED",
  Eliminado: "DELETED",
};

export const contactAccountTypeDictionary = {
  "Cuenta Corriente": "CURRENT_ACCOUNT",
  "Caja Ahorro": "SAVING_ACCOUNT",
  "Billetera Virtual": "DIGITAL_WALLET",
};
export const reversedContactAccountTypeDictionary = {
  CURRENT_ACCOUNT: "Cuenta corriente",
  SAVING_ACCOUNT: "Caja de ahorro",
  DIGITAL_WALLET: "Billetera digital",
};

export const getInitials = (name: string) => {
  if (!name) return "";

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  return `${words[0][0].toUpperCase()}${words[1][0].toUpperCase()}`;
};

export const handleFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result.split(",")[1]);
      } else {
        reject(new Error("El resultado del archivo no es un string"));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

export const genericErrorMessage =
  "Lo sentimos, algo falló. Intentá nuevamente o contactá a soporte.";

export const marketIsClosedErrorMessageTomorrow =
  "Lo sentimos, el mercado cerró. Intentalo mañana nuevamente.";

export const marketIsClosedErrorMessageNextDay =
  "Lo sentimos, el mercado cerró. Intentalo el próximo día hábil.";

export const onErrorGoToAllariaMas = (error?: string | null) => {
  const message =
    error && error == "user_not_allowed"
      ? "El usuario se encuentra suspendido o desvinculado de la cuenta a la cual pertenecias en Allaria+. Te recomendamos consultar con el administrador de la cuenta para ingresar nuevamente en ella."
      : genericErrorMessage;

  addMessage({
    messageId: "platformRedirectModal",
    message,
    type: "error",
  });
};

export const onErrorGoToAllariaEsco = () => {
  addMessage({
    messageId: "platformRedirectModal",
    message: genericErrorMessage,
    type: "error",
  });
};

export const handleTooltipsText = (state: string) => {
  switch (state) {
    case "MAIN_HOLDER_NOT_MATCHING_DNI_AND_IS_NOT_JURIDICAL":
      return <p>Solo los titulares pueden unificar saldos para esta cuenta</p>;
    case "MAIN_HOLDER_DOESNT_HAVE_FISCAL_ID":
      return (
        <p>Es necesario contar con CUIT/CUIL/CDI para poder unificar saldos</p>
      );
    case "MAIN_HOLDER_DOESNT_HAVE_EMAIL":
      return (
        <p>
          Es necesario actualizar la dirección de mail del cotitular.{" "}
          <a
            href="https://app.allaria.dev/login"
            target="_blank"
            style={{
              fontWeight: "bold",
              textDecoration: "underline",
              fontSize: "0.875rem",
            }}
          >
            Contactar a soporte
          </a>
        </p>
      );

    default:
      return <></>;
  }
};

export const isDev = import.meta.env.VITE_ENVIRONMENT === "dev";

export const getFormattedDateForDisplay = (date: DateValue | null) => {
  if (date) {
    const newDate = new Date(date.year, date.month - 1, date.day);

    const day = String(newDate.getDate()).padStart(2, "0");
    const month = String(newDate.getMonth() + 1).padStart(2, "0");
    const year = String(newDate.getFullYear()).slice(-2);

    return `${day}/${month}/${year}`;
  }
  return "Seleccionar fecha";
};

export const getFormattedPeriodForDisplay = (
  period: RangeValue<DateValue> | null
) => {
  if (period) {
    const newPeriodStart = new Date(
      period.start.year,
      period.start.month - 1,
      period.start.day
    );

    const day = String(newPeriodStart.getDate()).padStart(2, "0");
    const month = String(newPeriodStart.getMonth() + 1).padStart(2, "0");
    const year = String(newPeriodStart.getFullYear()).slice(-2);

    const newPeriodEnd = new Date(
      period.end.year,
      period.end.month - 1,
      period.end.day
    );

    const dayEnd = String(newPeriodEnd.getDate()).padStart(2, "0");
    const monthEnd = String(newPeriodEnd.getMonth() + 1).padStart(2, "0");
    const yearEnd = String(newPeriodEnd.getFullYear()).slice(-2);

    return `${day}/${month}/${year} - ${dayEnd}/${monthEnd}/${yearEnd}`;
  }
  return "Todos";
};

const getYesterday = () => today(getLocalTimeZone()).subtract({ days: 1 });

export const getRangeByPeriod = (period: string) => {
  const now = today(getLocalTimeZone());

  switch (period) {
    case "last-three-months":
      return {
        start: now.subtract({ months: 3 }),
        end: getYesterday(),
      };

    case "last-week":
      return {
        start: now.subtract({ weeks: 1 }),
        end: getYesterday(),
      };

    case "last-month":
      return {
        start: now.subtract({ months: 1 }),
        end: getYesterday(),
      };

    case "last-year":
      return {
        start: now.subtract({ years: 1 }),
        end: getYesterday(),
      };

    case "today":
      return { start: now, end: now };

    case "tomorrow":
      return { start: now.add({ days: 1 }), end: now.add({ days: 1 }) };

    case "last-fifteen-days":
      return { start: now.subtract({ days: 15 }), end: getYesterday() };

    case "all-history":
      return { start: now.subtract({ years: 3 }), end: getYesterday() };

    case "next-week":
      return { start: now, end: now.add({ weeks: 1 }) };

    case "next-fifteen-days":
      return { start: now, end: now.add({ days: 15 }) };

    case "next-month":
      return { start: now, end: now.add({ months: 1 }) };

    case "all-future":
      return { start: now, end: now.add({ years: 1 }) };

    default:
      return null;
  }
};

export const tabToInstrumentType: Record<
  string,
  { name: string; gender: string }
> = {
  STOCKS: { name: "acciones", gender: "f" },
  CEDEARS: { name: "CEDEARs", gender: "m" },
  SOVEREIGN_BONDS: { name: "bonos", gender: "m" },
  TREASURY_NOTES: { name: "letras", gender: "f" },
  CORPORATE_BONDS: { name: "corporativos", gender: "m" },
  OPTIONS: { name: "opciones", gender: "f" },
  REPO_OPERATION: { name: "cauciones", gender: "f" },
  FUNDS: { name: "fondos", gender: "m" },
};

export const instrumentToGender: Record<string, string> = {
  acciones: "f",
  CEDEARs: "m",
  bonos: "m",
  letras: "f",
  corporativos: "m",
  opciones: "f",
  cauciones: "f",
  fondos: "m",
};

export const asCurrency = (value?: number) =>
  !!value
    ? value?.toLocaleString("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : undefined;

export const asCuit = (value?: number | string) => {
  const string = value?.toString()?.replaceAll("-", "");
  return string?.length === 11
    ? `${string.substring(0, 2)}-${string.substring(2, 10)}-${string.substring(10)}`
    : undefined;
};

// For FileSystem Share Functions
export const blobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const onShareFile = async ({
  blob,
  fileName,
}: {
  blob: Blob;
  fileName: string;
}) => {
  let fileWritten = false;
  try {
    if (!blob) {
      return { success: false, error: "No blob provided" };
    }
    // Using the Web Share API for web platform
    // Use isNativePlatform() instead of getPlatform() === "web" to handle Live Reload correctly
    if (!Capacitor.isNativePlatform()) {
      const data = {
        files: [
          new File([blob], fileName, {
            type: blob.type,
          }),
        ],
      };
      await navigator.share(data);
      return { success: true };
    } else {
      let base64Data: string;
      try {
        base64Data = await blobToBase64(blob);
      } catch (err: unknown) {
        return {
          success: false,
          error: "Failed to convert blob to base64",
          details: err instanceof Error ? err.message : String(err),
        };
      }
      try {
        await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Cache,
        });
        fileWritten = true;
      } catch (err: unknown) {
        return {
          success: false,
          error: "Failed to write file",
          details: err instanceof Error ? err.message : String(err),
        };
      }

      let uriResult;
      try {
        uriResult = await Filesystem.getUri({
          path: fileName,
          directory: Directory.Cache,
        });
      } catch (err) {
        return {
          success: false,
          error: "Failed to get file URI",
          details: err,
        };
      }

      try {
        await Share.share({
          title: fileName,
          dialogTitle: "Compartir Archivo",
          url: uriResult.uri,
        });
      } catch (err: unknown) {
        if (
          err instanceof Error &&
          (err.name === "AbortError" ||
            err.message?.includes("abort") ||
            err.message?.includes("canceled"))
        ) {
          return {
            success: true, // Sharing was aborted by the user, not an error
          };
        }
        return {
          success: false,
          error: "Failed to share file",
          details: err instanceof Error ? err.message : String(err),
        };
      }
      return { success: true };
    }
  } catch (error: unknown) {
    if (
      error instanceof Error &&
      (error.name === "AbortError" || error.message?.includes("abort"))
    ) {
      return {
        success: true, // Sharing was aborted by the user, not an error
      };
    }
    return {
      success: false,
      error: "Unexpected error",
      details: (error instanceof Error ? error.message : undefined) || error,
    };
  } finally {
    // Only delete file on native platforms (iOS/Android)
    if (Capacitor.isNativePlatform() && fileWritten) {
      try {
        await Filesystem.deleteFile({
          path: fileName,
          directory: Directory.Cache,
        });
      } catch (e: unknown) {
        console.error(
          "Error deleting file:",
          e instanceof Error ? e.message : String(e)
        );
      }
    }
  }
};

export const coachmarkSectionIds = {
  firstTime: {
    activity: "first-time/activity/main",
    activityReport: "first-time/activity/report",
    activityTicket: "first-time/activity/ticket",
  },
};